import React, { useMemo } from 'react';

import {
  TbCalendarCheck,
  TbCalendarEvent,
  TbCheck,
  TbQuestionMark,
  TbSortDescending,
  TbX,
} from 'react-icons/tb';

import { useSearchParams } from 'react-router-dom';

import type { OrganisationPeriodJob } from 'state/__generated__/tadaahAPI';

import { createLocale } from 'locales';

import EmptyState from 'components-new/empty-state/empty-state';

import DropdownMenu from 'components-new/dropdown-menu/dropdown-menu';

import type { DropdownMenuItemType } from 'components-new/dropdown-menu/dropdown-menu-item';

import Button from 'components-new/button/button';

import { CREATE_JOB_URL } from 'constants/period-jobs-constant';

import Icon from 'components-new/icon/icon';

import PeriodJobCard from './PeriodJobCard';
import InfoBox from './InfoBox';

const periodJobsLocalize = createLocale('organisationPeriodJobsLocales');
const globalLocalize = createLocale('global');

interface PeriodJobsListProps {
  jobs: OrganisationPeriodJob[];
}

const PeriodJobsList: React.FC<PeriodJobsListProps> = (props) => {
  const { jobs } = props;
  const [searchParams, setSearchParams] = useSearchParams({});
  const dropdownMenuItems: DropdownMenuItemType[] = [
    {
      key: 'byStartDate',
      label: periodJobsLocalize('byStartDate'),
      leadingElement: <Icon icon={TbCalendarEvent} />,
      onClick: () => setSearchParams({ sort: 'by_start_date' }),
    },
    {
      key: 'byEndDate',
      label: periodJobsLocalize('byEndDate'),
      leadingElement: <Icon icon={TbCalendarCheck} />,
      onClick: () => setSearchParams({ sort: 'by_end_date' }),
    },
    {
      key: 'OpenJobsFirst',
      label: periodJobsLocalize('OpenJobsFirst'),
      leadingElement: <Icon icon={TbQuestionMark} />,
      onClick: () => setSearchParams({ sort: 'open_first' }),
    },
    {
      key: 'confirmedJobFirst',
      label: periodJobsLocalize('confirmedJobFirst'),
      leadingElement: <Icon icon={TbCheck} />,
      onClick: () => setSearchParams({ sort: 'confirmed_first' }),
    },
    {
      key: 'byLocation',
      label: periodJobsLocalize('byLocation'),
      leadingElement: <Icon icon="custLocation11" />,
      onClick: () => setSearchParams({ sort: 'by_location' }),
      variant: 'normal',
    },
    {
      key: 'clear',
      label: periodJobsLocalize('clearOrdering'),
      leadingElement: <Icon icon={TbX} />,
      onClick: () => setSearchParams({}),
      variant: 'destructive',
    },
  ];

  const sortLabel = useMemo(() => {
    switch (searchParams.get('sort')) {
      case 'by_start_date':
        return periodJobsLocalize('byStartDate');
      case 'by_end_date':
        return periodJobsLocalize('byEndDate');
      case 'by_location':
        return periodJobsLocalize('byLocation');
      case 'open_first':
        return periodJobsLocalize('OpenJobsFirst');
      case 'confirmed_first':
        return periodJobsLocalize('confirmedJobFirst');
      default:
        return periodJobsLocalize('sort');
    }
  }, [searchParams]);

  const sortedJobList = useMemo(() => {
    switch (searchParams.get('sort')) {
      case 'by_start_date': {
        return jobs.sort((job1, job2) => {
          if (job1.from_date > job2.from_date) return 1;
          return -1;
        });
      }
      case 'by_end_date':
        return jobs.sort((job1, job2) => {
          if (job1.to_date > job2.to_date) return 1;
          return -1;
        });

      case 'by_location':
        return jobs.sort((job1, job2) => {
          if (
            job1?.location &&
            job2.location &&
            job1?.location.toLowerCase() > job2?.location.toLowerCase()
          )
            return 1;
          return -1;
        });

      case 'open_first':
        return jobs.sort((job1, job2) => {
          if (job1.status === 'O' && job2.status !== 'O') return -1;
          return 1;
        });

      case 'confirmed_first':
        return jobs.sort((job1, job2) => {
          if (job1.is_confirmed && !job2.is_confirmed) return -1;
          return 1;
        });

      default: {
        return jobs.sort((job1, job2) => {
          if (job1.from_date > job2.from_date) return -1;
          return 1;
        });
      }
    }
  }, [jobs, searchParams]);

  return (
    <div className="w-[95%] md:w-full mt-1 my-b mx-6 md:mx-3">
      <InfoBox />
      {!jobs.length && (
        <div className="w-full flex flex-col items-center justify-center mt-6">
          <EmptyState
            variant="colorful"
            title={globalLocalize('noResultsFound')}
          />
          <Button
            label={periodJobsLocalize('newAssignement')}
            variant="primary"
            className=" mt-3"
            onClick={() => window.location.assign(CREATE_JOB_URL)}
          />
        </div>
      )}
      {!!jobs.length && (
        <div className="w-full flex items-center justify-end">
          <DropdownMenu
            menuButton={() => (
              <Button
                label={sortLabel}
                rightIconProps={{
                  className: 'text-gray-500',
                  icon: TbSortDescending,
                }}
                variant="transparent"
                className="hover:bg-transparent text-gray-500"
              />
            )}
            items={dropdownMenuItems}
            menuItemsClassName="z-50"
            position="bottom-end"
          />
        </div>
      )}
      {!!sortedJobList.length &&
        sortedJobList.map((job) => {
          return <PeriodJobCard key={job.id} job={job} />;
        })}
    </div>
  );
};

export default PeriodJobsList;
